import { Component, createRef } from "react"

/* Components */
import { DataLayout, Icon } from "../../components"

/* Widgets */
import { LeadModal, LeadForm } from "../../widgets/lead"
import { toast } from "../../widgets/common"

/* Helpers */
import { withRouter } from "../../helpers"

/* Shared */
import { STAGES, SOURCES, NICHES, stageOf, sourceOf, nextOf, titleOf, minutesText } from "../../widgets/lead/shared"
import { ago, initialsOf, avatarColorOf } from "../../widgets/user/shared"
import { plural } from "../../widgets/category/shared"

/* REST API */
import { list, stage } from "../../api/Lead"


/* The orders inside a column */
const SORTS = [
    { key: "new", text: "Новые" },
    { key: "next", text: "По звонку" },
    { key: "stale", text: "Давно без движения" }
]

/* What waits for the hand — the tiles filter the board */
const TILES = [
    { key: "today", icon: "clock", text: "позвонить сегодня", accent: "orange" },
    { key: "overdue", icon: "alert", text: "звонок просрочен", accent: "red" },
    { key: "untouched", icon: "sparkle", text: "никто не трогал", accent: "blue" },
    { key: "unassigned", icon: "profile", text: "без ответственного", accent: "violet" }
]

/* The empty filters */
const INITIAL = { search: "", view: "", source: "", niche: "", cityID: null, sort: "new" }


/* Page Leads — the sales pipeline. The businesses which may become paying points stand on a board
   by their stage: new → contacted → demo → won | lost. Most of them come by themselves: a request of
   the landing, a master who entered Pro and opened no point, a point which works unpaid. A card is
   dragged to another column or opened to call, write and plan the next call */
class Leads extends Component {

    constructor() {
        super()

        this.state = {
            status: "loading",
            board: null,
            counts: {},
            statistics: null,
            sources: [],
            niches: [],
            reasons: [],
            admins: [],
            cities: [],
            limit: 60,
            dragging: null,
            over: null,
            ...INITIAL
        }

        this._lead = createRef()
        this._form = createRef()
        this._timer = null
    }

    componentDidMount = () => {

        window.scrollTo(0, 0)
        this.load(true)
    }

    componentWillUnmount = () => {
        clearTimeout(this._timer)
    }

    load = (reload = false) => {

        /* Fields */
        const { search, view, source, niche, cityID, sort } = this.state

        if (reload) {
            this.setState({ status: "loading" })
        }

        list({ search, view, source, niche, cityID, sort }).then(response => {
            if (response && response.status === 200) {
                this.setState({
                    status: "ok",
                    board: response.data.board,
                    counts: response.data.counts,
                    statistics: response.data.statistics,
                    sources: response.data.sources,
                    niches: response.data.niches,
                    reasons: response.data.reasons,
                    admins: response.data.admins,
                    cities: response.data.cities,
                    limit: response.data.limit
                })
            }
            else {
                this.setState({ status: "error", board: null })
            }
        }).catch(() => {
            this.setState({ status: "network", board: null })
        })
    }

    refilter = fields => {
        this.setState(fields, () => this.load(true))
    }

    searchText = text => {

        clearTimeout(this._timer)
        this.setState({ search: text })

        this._timer = setTimeout(() => this.load(true), text.length === 0 ? 0 : 450)
    }

    /* A filter pressed twice lets go */
    toggle = (field, value, empty) => {
        this.refilter({ [field]: this.state[field] === value ? empty : value })
    }

    /* A card dropped on another column. The card moves at once, the server follows. «Отказ» needs
       a reason, so the card opens and asks */
    drop = key => {

        /* Fields */
        const { dragging, board } = this.state

        this.setState({ dragging: null, over: null })

        if (!dragging || dragging.stage === key) {
            return
        }

        if (key === "lost") {
            this._lead.current.open(dragging, true)
            return
        }

        const moved = { ...dragging, stage: key }

        const nextBoard = { ...board }
        nextBoard[dragging.stage] = board[dragging.stage].filter(item => item.id !== dragging.id)
        nextBoard[key] = [moved, ...board[key]]

        this.setState({ board: nextBoard })

        stage({ id: dragging.id, stage: key }).then(response => {
            if (response && response.status === 200) {
                toast.success(`${titleOf(dragging).title} → ${stageOf(key).short}`)
            }
            else {
                toast.error("Не удалось перенести")
            }
            this.load()
        }).catch(() => {
            toast.error("Не удалось перенести")
            this.load()
        })
    }

    _header = () => {

        /* Fields */
        const { search } = this.state

        return (
            <div className="organization-header">

                <div className="page-header-text">Лиды</div>

                <div className="organization-header-tools">

                    <form onSubmit={event => { event.preventDefault(); clearTimeout(this._timer); this.load(true) }} className="organization-search service-search">
                        <div className="organization-search-icon">
                            <Icon name="search" size={17} />
                        </div>
                        <input placeholder="Название, имя, телефон или заметка" value={search} onChange={event => this.searchText(event.target.value)} />
                        {search.length > 0 &&
                            <div onClick={() => this.searchText("")} className="organization-search-clear">
                                <Icon name="close" size={11} width={2.4} />
                            </div>
                        }
                    </form>

                    <div onClick={() => this._form.current.open()} className="organization-create">
                        <Icon name="plus" size={17} width={2.4} />
                        <span>Добавить</span>
                    </div>

                </div>

            </div>
        )
    }

    /* The pipeline at a glance — the funnel, what waits for a call and where the leads come from */
    _summary = () => {

        /* Fields */
        const { statistics, sources, view, source } = this.state

        if (!statistics) {
            return null
        }

        /* The funnel counts everyone who reached a stage, not only those standing on it */
        const reached = {
            new: statistics.total,
            contacted: statistics.contacted + statistics.demo + statistics.won,
            demo: statistics.demo + statistics.won,
            won: statistics.won
        }

        const steps = STAGES.filter(item => item.key !== "lost")
        const top = Math.max(reached.new, 1)
        const closed = statistics.won + statistics.lost

        return (
            <div className="lead-summary">

                <div className="lead-funnel">

                    <div className="lead-funnel-top">
                        <div>
                            <b>{statistics.open.toLocaleString("ru-RU")}</b>
                            <span>{plural(statistics.open, "лид", "лида", "лидов")} в работе · {statistics.total.toLocaleString("ru-RU")} всего</span>
                        </div>
                        <div className="lead-funnel-fresh">
                            <Icon name="trend" size={14} width={2.2} />
                            +{statistics.fresh} за 7 дней
                        </div>
                    </div>

                    <div className="lead-funnel-steps">
                        {steps.map((step, index) => {

                            const value = reached[step.key]
                            const previous = index > 0 ? reached[steps[index - 1].key] : 0
                            const conversion = index > 0 && previous > 0 ? Math.round(value / previous * 100) : null

                            return (
                                <div key={step.key} className={`lead-funnel-step ${step.accent}`}>
                                    <div className="lead-funnel-bar"><i style={{ height: `${Math.max(value / top * 100, 4)}%` }} /></div>
                                    <b>{value.toLocaleString("ru-RU")}</b>
                                    <span>{step.text}</span>
                                    {conversion !== null && <em>{conversion}%</em>}
                                </div>
                            )
                        })}
                    </div>

                    <div className="lead-funnel-foot">
                        <div><span>За 30 дней</span><b className="green">+{statistics.won30} подключено</b></div>
                        <div><span>Отказов за 30 дней</span><b>{statistics.lost30}</b></div>
                        <div><span>Конверсия закрытых</span><b>{closed > 0 ? `${Math.round(statistics.won / closed * 100)}%` : "—"}</b></div>
                        <div title="Сколько проходит от заявки с сайта или ручного лида до первого контакта"><span>Реакция на заявку</span><b>{minutesText(statistics.reaction)}</b></div>
                    </div>

                </div>

                <div className="lead-tiles">
                    {TILES.map(tile =>
                        <div
                            key={tile.key}
                            onClick={() => this.toggle("view", tile.key, "")}
                            className={`user-tile ${tile.accent} clickable ${view === tile.key ? "active" : ""} ${statistics[tile.key] > 0 ? "" : "calm"}`}
                        >
                            <div className="user-tile-icon"><Icon name={tile.icon} size={16} width={2} /></div>
                            <b>{statistics[tile.key].toLocaleString("ru-RU")}</b>
                            <span>{tile.text}</span>
                        </div>
                    )}
                </div>

                <div className="lead-sources">
                    <div className="lead-sources-title">Откуда лиды</div>
                    {Object.keys(SOURCES).map(key => {

                        const meta = SOURCES[key]
                        const row = sources.find(item => item.key === key)

                        return (
                            <div key={key} onClick={() => this.toggle("source", key, "")} className={`lead-source ${meta.accent} ${source === key ? "active" : ""}`} title={meta.hint}>
                                <div className="lead-source-icon"><Icon name={meta.icon} size={15} width={2} /></div>
                                <div className="lead-source-text">
                                    <b>{meta.text}</b>
                                    <span>{row ? `${row.open} в работе · ${row.won} ${plural(row.won, "подключён", "подключено", "подключено")}` : "пока нет"}</span>
                                </div>
                                <div className="lead-source-count">{row ? row.total : 0}</div>
                            </div>
                        )
                    })}
                </div>

            </div>
        )
    }

    _toolbar = () => {

        /* Fields */
        const { view, sort, statistics, niche, niches, cityID, cities } = this.state

        return (
            <>
                <div className="service-toolbar">

                    <div className="service-segment">
                        <div onClick={() => this.refilter({ view: "" })} className={view !== "mine" ? "active" : ""}>Все</div>
                        <div onClick={() => this.refilter({ view: "mine" })} className={view === "mine" ? "active" : ""}>
                            Мои
                            {statistics && statistics.mine > 0 && <span>{statistics.mine}</span>}
                        </div>
                    </div>

                    <div className="service-sort">
                        <Icon name="sort" size={15} width={1.9} />
                        {SORTS.map(item =>
                            <div key={item.key} onClick={() => this.refilter({ sort: item.key })} className={sort === item.key ? "active" : ""}>{item.text}</div>
                        )}
                    </div>

                </div>

                {(cities.length > 0 || niches.length > 0) &&
                    <div className="lead-filters">
                        {cities.map(city =>
                            <div key={`city-${city.id}`} onClick={() => this.toggle("cityID", parseInt(city.id), null)} className={`lead-chip small ${cityID === parseInt(city.id) ? "active" : ""}`}>{city.name}</div>
                        )}
                        {niches.length > 0 && <i />}
                        {niches.filter(item => NICHES[item.key]).map(item =>
                            <div key={`niche-${item.key}`} onClick={() => this.toggle("niche", item.key, "")} className={`lead-chip small ${niche === item.key ? "active" : ""}`}>{NICHES[item.key]}<em>{item.total}</em></div>
                        )}
                    </div>
                }
            </>
        )
    }

    /* One card of the board */
    _card = item => {

        /* Fields */
        const { dragging } = this.state

        const { title, subtitle } = titleOf(item)
        const source = sourceOf(item.source)
        const due = item.stage === "won" || item.stage === "lost" ? null : nextOf(item.nextAt)

        return (
            <div
                key={`${item.id}`}
                draggable
                onDragStart={event => { event.dataTransfer.effectAllowed = "move"; this.setState({ dragging: item }) }}
                onDragEnd={() => this.setState({ dragging: null, over: null })}
                onClick={() => this._lead.current.open(item)}
                className={`lead-card ${dragging && dragging.id === item.id ? "dragging" : ""} ${due && due.late ? "late" : ""}`}
            >

                <div className="lead-card-top">
                    <div className="lead-card-title">
                        <b>{title}</b>
                        {subtitle && <span>{subtitle}</span>}
                    </div>
                    <div className={`lead-card-source ${source.accent}`} title={source.text}><Icon name={source.icon} size={12} width={2.2} /></div>
                </div>

                <div className="lead-card-meta">
                    {item.phone && <span>{item.phone}</span>}
                    {item.city && <span>{item.city}</span>}
                    {item.niche && NICHES[item.niche] && <span>{NICHES[item.niche]}</span>}
                </div>

                {item.lastNote && <div className="lead-card-note">{item.lastNote}</div>}
                {!item.lastNote && item.comment && <div className="lead-card-note">{item.comment}</div>}

                <div className="lead-card-footer">

                    {item.admin
                        ? <i className="lead-card-admin" style={{ background: avatarColorOf(item.adminID) }} title={item.admin}>{initialsOf(item.admin)}</i>
                        : <i className="lead-card-admin empty" title="Без ответственного"><Icon name="profile" size={11} width={2.2} /></i>
                    }

                    {due
                        ? <div className={`lead-due ${due.accent}`}><Icon name="clock" size={11} width={2.4} />{due.text}</div>
                        : <span className="lead-card-age">{item.stage === "lost" && item.lostReason ? "" : ago(item.lastEventAt || item.createdAt)}</span>
                    }

                    {item.touches > 0 && <span className="lead-card-touches" title="Звонков и заметок"><Icon name="phone" size={11} width={2.2} />{item.touches}</span>}

                </div>

            </div>
        )
    }

    _board = () => {

        /* Fields */
        const { board, counts, dragging, over } = this.state

        return (
            <div className="lead-board">
                {STAGES.map(column => {

                    const cards = board[column.key] || []
                    const count = counts[column.key] || 0

                    return (
                        <div
                            key={column.key}
                            onDragOver={event => { if (dragging) { event.preventDefault(); if (over !== column.key) { this.setState({ over: column.key }) } } }}
                            onDragLeave={event => { if (!event.currentTarget.contains(event.relatedTarget)) { this.setState({ over: null }) } }}
                            onDrop={event => { event.preventDefault(); this.drop(column.key) }}
                            className={`lead-column ${column.accent} ${over === column.key && dragging && dragging.stage !== column.key ? "over" : ""}`}
                        >

                            <div className="lead-column-head" title={column.hint}>
                                <i />
                                <b>{column.text}</b>
                                <span>{count}</span>
                            </div>

                            <div className="lead-column-cards">
                                {cards.map(item => this._card(item))}
                                {cards.length === 0 && <div className="lead-column-empty">{dragging ? "Перетащите сюда" : "Пусто"}</div>}
                                {count > cards.length && <div className="lead-column-more">ещё {count - cards.length} — уточните поиском</div>}
                            </div>

                        </div>
                    )
                })}
            </div>
        )
    }

    _content = () => {

        /* Fields */
        const { status, board } = this.state

        if (status !== "ok" || !board) {
            return (
                <div className="page-content">
                    <DataLayout status={status} reload={() => this.load(true)} data={[]} />
                </div>
            )
        }

        return (
            <div className="page-content">
                {this._summary()}
                {this._toolbar()}
                {this._board()}
            </div>
        )
    }

    render = () => {

        /* Fields */
        const { navigate } = this.props
        const { admins, cities } = this.state

        return (
            <div className="page">

                {this._header()}

                {this._content()}

                <LeadModal
                    ref={this._lead}
                    admins={admins}
                    onChanged={() => this.load()}
                    onEdit={lead => this._form.current.open(lead)}
                    onOrganization={id => navigate(`/organization?open=${id}`)}
                />

                <LeadForm
                    ref={this._form}
                    cities={cities}
                    onSaved={id => { this.load(); this._lead.current.open({ id }) }}
                    onDuplicate={id => this._lead.current.open({ id })}
                />

            </div>
        )
    }
}

export default withRouter(Leads)
